import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Mail, Lock, User, Phone, Key, CheckCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { API } from '../config/api';

function Register() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const invitationCodeFromUrl = searchParams.get('code') || '';
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    invitation_code: invitationCodeFromUrl
  });
  const [isLoading, setIsLoading] = useState(false);
  const [invitationData, setInvitationData] = useState(null);
  const [loadingInvitation, setLoadingInvitation] = useState(false);

  // Fetch invitation details when code is available
  useEffect(() => {
    const fetchInvitationDetails = async () => {
      if (!invitationCodeFromUrl) return;
      
      setLoadingInvitation(true);
      try {
        const response = await fetch(`${API}/invitations/${invitationCodeFromUrl}/details`);
        if (response.ok) {
          const data = await response.json();
          setInvitationData(data);
          // Pre-fill form with invitation data
          setFormData(prev => ({
            ...prev,
            name: data.invitee_name || '',
            email: data.email || '',
            phone: data.invitee_phone || '',
            invitation_code: invitationCodeFromUrl
          }));
        }
      } catch (error) {
        console.error('Failed to fetch invitation details:', error);
      } finally {
        setLoadingInvitation(false);
      }
    };
    
    fetchInvitationDetails();
  }, [invitationCodeFromUrl]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate password confirmation
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    setIsLoading(true);

    try {
      const response = await fetch(`${API}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone || null,
          password: formData.password,
          invitation_code: formData.invitation_code
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Registration failed');
      }

      const user = await response.json();
      toast.success('Registration successful! Welcome to the portal.');
      navigate('/dashboard', { state: { user } });
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Show loading while fetching invitation
  if (loadingInvitation) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFCFE]">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-[#80298F] mx-auto mb-4" />
          <p className="text-gray-600">Loading invitation details...</p>
        </div>
      </div>
    );
  }

  // Simplified form for clients with invitation (only password needed)
  const hasInvitation = invitationData && invitationData.email;

  return (
    <div className="min-h-screen flex items-center justify-center p-4 sm:p-8 bg-[#FDFCFE]">
      <div className="w-full max-w-md">
        {hasInvitation ? (
          // Simplified registration for invited clients
          <>
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-[#80298F]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-8 w-8 text-[#80298F]" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 mb-2" data-testid="register-heading">
                Welcome, {invitationData.invitee_name}!
              </h2>
              <p className="text-gray-600">Create a password to complete your registration</p>
            </div>

            {/* Show invitation details */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6 space-y-2">
              <div className="flex items-center gap-2 text-sm">
                <User className="h-4 w-4 text-gray-400" />
                <span className="text-gray-600">Name:</span>
                <span className="font-medium text-gray-900">{invitationData.invitee_name}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Mail className="h-4 w-4 text-gray-400" />
                <span className="text-gray-600">Email:</span>
                <span className="font-medium text-gray-900">{invitationData.email}</span>
              </div>
              {invitationData.case_name && (
                <div className="flex items-center gap-2 text-sm">
                  <Key className="h-4 w-4 text-gray-400" />
                  <span className="text-gray-600">Case:</span>
                  <span className="font-medium text-gray-900">{invitationData.case_name}</span>
                </div>
              )}
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                  Create Password *
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <Input
                    id="password"
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className="pl-10 h-11"
                    placeholder="Minimum 8 characters"
                    required
                    minLength={8}
                    data-testid="password-input"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm Password *
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <Input
                    id="confirmPassword"
                    type="password"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className="pl-10 h-11"
                    placeholder="Re-enter your password"
                    required
                    data-testid="confirm-password-input"
                  />
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full h-11 bg-[#80298F] hover:bg-[#6B2278] text-white mt-6"
                disabled={isLoading}
                data-testid="register-submit-button"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Creating Account...
                  </>
                ) : (
                  'Create Account'
                )}
              </Button>
            </form>
          </>
        ) : (
          // Full registration form (for staff/admin or manual registration)
          <>
            <div className="text-center mb-8">
              <h2 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 mb-2" data-testid="register-heading">
                Create Account
              </h2>
              <p className="text-gray-600">Register with your invitation code</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="invitation_code" className="block text-sm font-medium text-gray-700 mb-1">
                  Invitation Code *
                </label>
                <div className="relative">
                  <Key className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <Input
                    id="invitation_code"
                    type="text"
                    name="invitation_code"
                    value={formData.invitation_code}
                    onChange={handleChange}
                    className="pl-10 h-11 font-mono"
                    placeholder="Enter your invitation code"
                    required
                    data-testid="invitation-code-input"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name *
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <Input
                    id="name"
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="pl-10 h-11"
                    placeholder="John Doe"
                    required
                    data-testid="name-input"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email *
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <Input
                    id="email"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="pl-10 h-11"
                    placeholder="your.email@example.com"
                    required
                    data-testid="email-input"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                  Phone (Optional)
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <Input
                    id="phone"
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="pl-10 h-11"
                    placeholder="(555) 123-4567"
                    data-testid="phone-input"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                  Password *
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <Input
                    id="password"
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className="pl-10 h-11"
                    placeholder="Minimum 8 characters"
                    required
                    minLength={8}
                    data-testid="password-input"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm Password *
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <Input
                    id="confirmPassword"
                    type="password"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className="pl-10 h-11"
                    placeholder="Re-enter your password"
                    required
                    data-testid="confirm-password-input"
                  />
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full h-11 bg-[#80298F] hover:bg-[#6B2278] text-white mt-2"
                disabled={isLoading}
                data-testid="register-submit-button"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Creating Account...
                  </>
                ) : (
                  'Create Account'
                )}
              </Button>
            </form>
          </>
        )}

        <p className="mt-6 text-center text-sm text-gray-600">
          Already have an account?{' '}
          <Link to="/login" className="font-medium text-[#80298F] hover:text-[#6B2278]" data-testid="login-link">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}

export default Register;
